import { Annotation } from '../models/annotation.model';
import { AnnotationRelation } from '../models/annotation-relation.model';
import { Map } from 'immutable';

type NestPair = { left: string; right: string };

/**
 * Computes parent-child relation pairs for auto-nesting.
 *
 * If some selected annotations are already nested among each other,
 * the existing nesting determines the type hierarchy (schema). Those
 * already-nested annotations and their parents are excluded, and the
 * schema is applied to the remaining flat annotations.
 *
 * If all selected annotations are flat, the last distinct type in
 * selection order is the parent type, and all others are nested
 * directly under the nearest annotation of that type.
 */
export function computeAutoNestPairs(
  selectedIds: string[],
  annotations: Map<string, Annotation>,
  relations: AnnotationRelation[],
): NestPair[] {
  const selectedSet = new Set(selectedIds);

  // Find existing child-of relations among selected annotations
  const existingNesting = relations.filter(
    r =>
      r.rule.type === 'child-of' &&
      selectedSet.has(r.left.id) &&
      selectedSet.has(r.right.id),
  );

  if (existingNesting.length > 0) {
    return nestFromSchema(selectedIds, annotations, existingNesting);
  }

  return nestFlat(selectedIds, annotations);
}

/**
 * Flat case: last type in selection order is the parent.
 * All other annotations nest directly under nearest parent-type annotation.
 */
function nestFlat(
  selectedIds: string[],
  annotations: Map<string, Annotation>,
): NestPair[] {
  const typeOrder: string[] = [];
  for (const id of selectedIds) {
    const ann = annotations.get(id);
    if (!ann) continue;
    if (typeOrder.indexOf(ann.type) < 0) {
      typeOrder.push(ann.type);
    }
  }
  if (typeOrder.length < 2) return [];

  const parentType = typeOrder[typeOrder.length - 1];
  const parents: Annotation[] = [];
  const children: Annotation[] = [];

  for (const id of selectedIds) {
    const ann = annotations.get(id);
    if (!ann) continue;
    if (ann.type === parentType) {
      if (!parents.some(a => a.id === ann.id)) parents.push(ann);
    } else {
      if (!children.some(a => a.id === ann.id)) children.push(ann);
    }
  }

  parents.sort((a, b) => (a.start || 0) - (b.start || 0));
  children.sort((a, b) => (a.start || 0) - (b.start || 0));

  if (parents.length === 0 || children.length === 0) return [];
  return assignChildrenToParents(children, parents);
}

/**
 * Schema case: derive type hierarchy from existing nesting among
 * selected annotations. Exclude already-nested annotations and
 * their parents, then apply the schema to the remaining flat ones.
 */
function nestFromSchema(
  selectedIds: string[],
  annotations: Map<string, Annotation>,
  existingNesting: AnnotationRelation[],
): NestPair[] {
  // Build type hierarchy from existing relations: child-type → parent-type
  const typeSchema: { [childType: string]: string } = {};
  for (const rel of existingNesting) {
    const childType = rel.left.type;
    const parentType = rel.right.type;
    if (childType && parentType) {
      typeSchema[childType] = parentType;
    }
  }

  // Collect IDs already involved in nesting (exclude from auto-nest)
  const excludeIds = new Set<string>();
  for (const rel of existingNesting) {
    excludeIds.add(rel.left.id);
    excludeIds.add(rel.right.id);
  }

  // Remaining annotations to auto-nest
  const remaining: Annotation[] = [];
  for (const id of selectedIds) {
    if (excludeIds.has(id)) continue;
    const ann = annotations.get(id);
    if (ann) remaining.push(ann);
  }

  // Group remaining by type
  const byType: { [type: string]: Annotation[] } = {};
  for (const ann of remaining) {
    if (!byType[ann.type]) byType[ann.type] = [];
    byType[ann.type].push(ann);
  }
  for (const type of Object.keys(byType)) {
    byType[type].sort((a, b) => (a.start || 0) - (b.start || 0));
  }

  // Apply schema: for each child-type → parent-type mapping
  const allPairs: NestPair[] = [];
  for (const childType of Object.keys(typeSchema)) {
    const parentType = typeSchema[childType];
    const children = byType[childType] || [];
    const parents = byType[parentType] || [];
    if (children.length === 0 || parents.length === 0) continue;

    allPairs.push(...assignChildrenToParents(children, parents));

    // Parents used in this level become excluded for the next level
    // (they're now children themselves if they appear in the schema)
  }

  return allPairs;
}

function assignChildrenToParents(
  children: Annotation[],
  parents: Annotation[],
): NestPair[] {
  if (children.length <= parents.length) {
    return assignGroups(
      children.map(c => ({
        ids: [c.id],
        start: c.start || 0,
        end: c.end || 0,
      })),
      parents,
    );
  }

  const groups = groupChildrenForParents(children, parents.length);
  return assignGroups(groups, parents);
}

type ChildGroup = { ids: string[]; start: number; end: number };

function groupChildrenForParents(
  children: Annotation[],
  parentCount: number,
): ChildGroup[] {
  let groups: ChildGroup[] = children.map(c => ({
    ids: [c.id],
    start: c.start || 0,
    end: c.end || 0,
  }));

  if (groups.length <= parentCount) return groups;

  while (groups.length > parentCount) {
    let minGap = Infinity;
    let minIdx = 0;
    for (let i = 0; i < groups.length - 1; i++) {
      const gap = groups[i + 1].start - groups[i].end;
      if (gap < minGap) {
        minGap = gap;
        minIdx = i;
      }
    }

    const merged: ChildGroup = {
      ids: groups[minIdx].ids.concat(groups[minIdx + 1].ids),
      start: groups[minIdx].start,
      end: groups[minIdx + 1].end,
    };
    groups = [...groups.slice(0, minIdx), merged, ...groups.slice(minIdx + 2)];
  }

  return groups;
}

function assignGroups(groups: ChildGroup[], parents: Annotation[]): NestPair[] {
  const pairs: NestPair[] = [];

  const sortedGroups = groups.slice().sort((a, b) => a.start - b.start);
  const sortedParents = parents
    .slice()
    .sort((a, b) => (a.start || 0) - (b.start || 0));

  const K = sortedGroups.length;
  const N = sortedParents.length;
  let lastAssigned = -1;

  for (let i = 0; i < K; i++) {
    const groupMid = (sortedGroups[i].start + sortedGroups[i].end) / 2;
    const maxIdx = N - K + i;
    let bestIdx = lastAssigned + 1;
    let bestDist = Infinity;
    for (let j = lastAssigned + 1; j <= maxIdx; j++) {
      const parentMid =
        ((sortedParents[j].start || 0) + (sortedParents[j].end || 0)) / 2;
      const dist = Math.abs(groupMid - parentMid);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = j;
      }
    }
    lastAssigned = bestIdx;
    for (const childId of sortedGroups[i].ids) {
      pairs.push({ left: childId, right: sortedParents[bestIdx].id });
    }
  }

  return pairs;
}
